// ─────────────────────────────────────────────────────────────
// Manages beneficiary lists in localStorage per service type.
// Each entry is keyed by a stable ID derived from the unique
// identifier (phone, meter number, smartcard).
//
// Storage key: "beneficiaries_{type}" e.g. "beneficiaries_airtime"
//
// Entry shape:
// {
//   id:          string,   // unique: phone / meterNumber / smartcardNo
//   type:        string,   // "airtime" | "data" | "electricity" | "tv"
//   label:       string,   // customer name from verification or phone number
//   lastUsed:    number,   // timestamp
//   // type-specific fields:
//   phone?:      string,
//   network?:    string,
//   disco?:      string,
//   meterNumber?: string,
//   meterType?:  string,
//   provider?:   string,
//   smartcardNo?: string,
// }
// ─────────────────────────────────────────────────────────────

import { useState, useCallback } from "react";

const MAX_PER_TYPE = 10; // keep last 10 beneficiaries per service

function storageKey(type) {
  return `beneficiaries_${type}`;
}

function load(type) {
  try {
    const raw = localStorage.getItem(storageKey(type));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function save(type, list) {
  try { localStorage.setItem(storageKey(type), JSON.stringify(list)); }
  catch { /* storage full — skip silently */ }
}

export function useBeneficiaries(type) {
  const [beneficiaries, setBeneficiaries] = useState(() => load(type));

  // Most recent first
  const sorted = [...beneficiaries].sort((a, b) => b.lastUsed - a.lastUsed);

  const addOrUpdate = useCallback((entry) => {
    if (!entry.id) return;
    setBeneficiaries(prev => {
      const filtered = prev.filter(b => b.id !== entry.id);
      const updated  = [{ ...entry, type, lastUsed: Date.now() }, ...filtered]
        .slice(0, MAX_PER_TYPE);
      save(type, updated);
      return updated;
    });
  }, [type]);

  const remove = useCallback((id) => {
    setBeneficiaries(prev => {
      const updated = prev.filter(b => b.id !== id);
      save(type, updated);
      return updated;
    });
  }, [type]);

  const mostRecent = sorted[0] || null;

  return { beneficiaries: sorted, mostRecent, addOrUpdate, remove };
}

// ── Helpers called from bill pages after successful payment ───

export function saveBeneficiary(entry) {
  if (!entry.type || !entry.id) return;
  try {
    const key  = storageKey(entry.type);
    const raw  = localStorage.getItem(key);
    const list = raw ? JSON.parse(raw) : [];
    const filtered = list.filter(b => b.id !== entry.id);
    const updated  = [{ ...entry, lastUsed: Date.now() }, ...filtered].slice(0, MAX_PER_TYPE);
    localStorage.setItem(key, JSON.stringify(updated));
  } catch {}
}
